import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useTranslation } from "../translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FreeHighlightDialog = ({ open, onOpenChange, onUsed }) => {
  const { t } = useTranslation();
  const [ads, setAds] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchAds = async () => {
      setLoading(true);
      setSelected(null);
      try {
        const [jobsRes, mktRes] = await Promise.all([
          axios.get(`${API}/my-jobs`).catch(() => ({ data: [] })),
          axios.get(`${API}/my-marketplace`).catch(() => ({ data: [] })),
        ]);
        const jobs = (jobsRes.data || []).map((j) => ({
          id: j.id,
          title: j.title,
          type: "job",
          featured: j.is_featured,
        }));
        const items = (mktRes.data || []).map((m) => ({
          id: m.id,
          title: m.title,
          type: "marketplace",
          featured: m.is_featured,
        }));
        setAds([...jobs, ...items]);
      } catch {
        setAds([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, [open]);

  const handleApply = async () => {
    if (!selected) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/free-highlights/use`, { target_id: selected });
      toast.success(res.data.message || t("freeHighlightSuccess"));
      onOpenChange(false);
      if (onUsed) onUsed();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("freeHighlightError"));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="free-highlight-dialog">
        <DialogHeader>
          <DialogTitle>✨ {t("useFreeHighlight")}</DialogTitle>
          <DialogDescription>{t("chooseAdToHighlight")}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-gray-500 py-4 text-center">{t("loading")}</p>
        ) : ads.length === 0 ? (
          <p className="text-sm text-gray-600 py-4 text-center" data-testid="no-ads-message">
            {t("noAdsToHighlight")}
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {ads.map((ad) => (
              <button
                key={ad.id}
                type="button"
                data-testid={`highlight-ad-option-${ad.id}`}
                onClick={() => setSelected(ad.id)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                  selected === ad.id
                    ? "border-amber-500 bg-amber-50"
                    : "border-gray-200 hover:border-amber-300"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{ad.title}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    {ad.featured && <Badge className="bg-amber-500 text-white">⭐</Badge>}
                    <Badge variant="outline">
                      {ad.type === "job" ? t("jobBoardLabel") : t("marketplaceLabel")}
                    </Badge>
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
        <Button
          data-testid="apply-free-highlight-btn"
          className="w-full bg-amber-500 hover:bg-amber-600"
          disabled={!selected || applying}
          onClick={handleApply}
        >
          {applying ? t("applying") : t("applyFreeHighlight")}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
